import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Heart } from "lucide-react";
import { AssetImage } from "@/components/site/AssetImage";
import { formatUSD, progressPercent } from "@/lib/format";
import band from "@/assets/fundraiser-band.webp";
import { fetchFeaturedFundraisers } from "@/lib/publicFundraisers";

export function FundraiserBand() {
  const { data: fundraisers, isError } = useQuery({
    queryKey: ["fundraisers_home_featured"],
    queryFn: async () => (await fetchFeaturedFundraisers(3)).data,
    retry: 3,
    retryDelay: (attempt) => Math.min(800 * 2 ** attempt, 5000),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // If the backend was unavailable after retries, hide the strip rather than
  // showing an empty/broken section on the landing page.
  if (isError && !fundraisers?.length) return null;

  return (
    <section className="py-24">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-3xl text-center">
          <span className="text-sm font-semibold uppercase tracking-widest text-primary">
            Community fundraisers
          </span>
          <h2 className="mt-3 font-display text-3xl font-semibold leading-tight md:text-4xl lg:text-5xl text-balance">
            Real people raising for Sri Lanka
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg text-pretty">
            Birthdays, marathons, weddings, tributes — back a fundraiser started by
            our community, or launch your own.
          </p>
        </div>

        {!!fundraisers?.length && (
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {fundraisers.map((f: any) => {
              const pct = progressPercent(f.raised_cents, f.goal_cents);
              return (
                <Link
                  key={f.id}
                  to={`/fundraisers/${f.slug}`}
                  className="group flex flex-col overflow-hidden rounded-2xl bg-card shadow-card transition-all hover:-translate-y-1 hover:shadow-soft"
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                    {f.hero_image && (
                      <AssetImage
                        src={f.hero_image}
                        alt={f.title}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-display text-lg font-semibold leading-snug">
                      {f.title}
                    </h3>
                    <div className="mt-4">
                      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-accent"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="mt-2 flex justify-between text-xs">
                        <span className="font-semibold">
                          {formatUSD(f.raised_cents)}{" "}
                          <span className="font-normal text-muted-foreground">
                            of {formatUSD(f.goal_cents)}
                          </span>
                        </span>
                        <span className="font-semibold text-primary">{pct}%</span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <Button
            asChild
            variant="outline"
            className="rounded-full"
          >
            <Link to="/fundraisers">Browse all fundraisers</Link>
          </Button>
          <Button
            asChild
            className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
          >
            <Link to="/start-a-fundraiser">Start a fundraiser</Link>
          </Button>
        </div>

        <div className="mt-16 group relative overflow-hidden rounded-3xl bg-foreground p-10 text-background md:p-14">
          <img
            src={band}
            alt=""
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover opacity-30 transition-transform duration-700 group-hover:scale-105"
          />
          <div className="relative max-w-2xl">
            <Heart className="h-10 w-10 text-accent" />
            <h3 className="mt-6 font-display text-3xl font-semibold leading-tight md:text-4xl">
              Turn any moment into hope
            </h3>
            <p className="mt-3 text-background/80">
              Birthday, marathon, wedding gift, in-memory tribute — every
              fundraiser becomes real impact for a Sri Lankan family.
            </p>
            <Button
              asChild
              className="mt-7 rounded-full bg-background text-foreground hover:bg-background/90"
            >
              <Link to="/start-a-fundraiser">Start your fundraiser</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
